"use client";
import React, { Suspense, useState, useEffect, FunctionComponent } from 'react';
import dynamic from "next/dynamic";
import { useInView } from 'react-intersection-observer';
import { Helpers, Providers } from "@tpd-web-common-libs/nodejs-library";
import TreePageLayout from "./TreePageLayout";
import { SingleColumnLayout } from "./SingleColumnLayout";
import { ConfiguratoreCasaFormLayout } from "./ConfiguratoreCasaFormLayout";
import { CustomTemplateLayout } from "./CustomLayout";

const Remote = dynamic(() => import("./__shared__/Remote/Remote"));

const NOT_FOUND_LAYOUT = ({
  layout,
  id,
  title,
}: Omit<Providers.PageLayouts.TNextPage, "header" | "footer">) => (
  <div>{`Error: The Layout '${layout}' of page with ID '${id}' and title '${title}' is not handle!`}</div>
);

const LAYOUT_MAP: Record<string, FunctionComponent<any>> = {
  [Providers.PageLayouts.TreePage.TREE_PAGE_LAYOUT_TYPE]: TreePageLayout,
  [Providers.PageLayouts.CmsPage.Layouts.SINGLE_COLUMN_LAYOUT_TYPE]: SingleColumnLayout,
  [Providers.PageLayouts.CmsPage.Layouts.CONFIGURATORE_CASA_FORM_LAYOUT_TYPE]: ConfiguratoreCasaFormLayout,
  [Providers.PageLayouts.CmsPage.Layouts.HP_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.ONECOLUMN_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.CUSTOM_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.UTILE_A_SAPERSI_FOGLIA_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.PREVENTIVATORE_AUTO_FORM_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.QUOTATORI_FORM_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
  [Providers.PageLayouts.CmsPage.Layouts.ASSICURAZIONE_AUTO_ADV_TEMPLATE_LAYOUT_TYPE]: CustomTemplateLayout,
};

export default function PageLayout({
  header,
  footer,
  ...props
}: Providers.PageLayouts.TNextPage) {
  const [isBot, setIsBot] = useState(false);

  const { ref: headerRef, inView: headerVisible } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px'
  });

  const { ref: footerRef, inView: footerVisible } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px'
  });

  useEffect(() => {
    const ua = navigator.userAgent;
    if (ua.includes("Google PageSpeed Insights") || ua.includes("Chrome-Lighthouse")) {
      setIsBot(true);
    }
  }, []);

  const Layout: FunctionComponent<Providers.PageLayouts.TNextPageLayoutType> =
    props.layout in LAYOUT_MAP ? LAYOUT_MAP[props.layout] : NOT_FOUND_LAYOUT;

  return (
    <>
      <div ref={headerRef}>
        <Remote
          componentProps={Helpers.RemoteHelper.getComponentPropsBy(header, props)}
        />
      </div>
      <Layout {...props} />

      <div ref={footerRef}>
        {(footerVisible || isBot) && (
          <Suspense fallback={<div style={{ minHeight: '300px' }} />}>
            <Remote
              componentProps={Helpers.RemoteHelper.getComponentPropsBy(footer, props)}
            />
          </Suspense>
        )}
      </div>
    </>
  );
}
